'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, Check, X, CircleDot, Footprints, Hand, Backpack } from 'lucide-react';
import type { PlayerGearProfile, UpdateGearProfileData } from '@/hooks/use-parent-data';
import {
  RACQUET_BRANDS, RACQUET_MODELS, GRIP_SIZES,
  STRING_BRANDS, STRING_TYPES, STRING_TENSIONS,
  SHOE_BRANDS, SHOE_MODELS, SHOE_SIZES,
  PLAYING_STYLES, DOMINANT_HANDS, BACKHAND_TYPES,
} from '@/shared/constants/gear-options';

interface GearProfileCardProps {
  profile: PlayerGearProfile | null;
  isLoading: boolean;
  onUpdate: (data: UpdateGearProfileData) => Promise<void>;
  isUpdating: boolean;
}

export function GearProfileCard({
  profile,
  isLoading,
  onUpdate,
  isUpdating,
}: GearProfileCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateGearProfileData>({});

  // Get available models based on selected brand
  const availableRacquetModels = formData.racquetBrand
    ? RACQUET_MODELS[formData.racquetBrand] || []
    : [];
  const availableShoeModels = formData.shoeBrand
    ? SHOE_MODELS[formData.shoeBrand] || []
    : [];

  const startEditing = () => {
    setFormData({
      racquetBrand: profile?.racquetBrand || '',
      racquetModel: profile?.racquetModel || '',
      gripSize: profile?.gripSize || '',
      stringBrand: profile?.stringBrand || '',
      stringType: profile?.stringType || '',
      stringTension: profile?.stringTension || '',
      shoeBrand: profile?.shoeBrand || '',
      shoeModel: profile?.shoeModel || '',
      shoeSize: profile?.shoeSize || '',
      playingStyle: profile?.playingStyle || null,
      dominantHand: profile?.dominantHand || null,
      backhandType: profile?.backhandType || null,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setFormData({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    await onUpdate(formData);
    setIsEditing(false);
  };

  const updateField = (field: keyof UpdateGearProfileData, value: string | null) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value || null };
      // Reset model when brand changes
      if (field === 'racquetBrand') {
        updated.racquetModel = null;
      }
      if (field === 'shoeBrand') {
        updated.shoeModel = null;
      }
      return updated;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-4">
        {/* Racquet Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Backpack className="w-5 h-5" />
              Racquet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand</Label>
                <Select
                  value={formData.racquetBrand || ''}
                  onValueChange={(value) => updateField('racquetBrand', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {RACQUET_BRANDS.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={formData.racquetModel || ''}
                  onValueChange={(value) => updateField('racquetModel', value)}
                  disabled={!formData.racquetBrand}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.racquetBrand ? "Select model" : "Select brand first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRacquetModels.map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Grip Size</Label>
              <Select
                value={formData.gripSize || ''}
                onValueChange={(value) => updateField('gripSize', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select grip size" />
                </SelectTrigger>
                <SelectContent>
                  {GRIP_SIZES.map(size => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Strings Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CircleDot className="w-5 h-5" />
              Strings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand</Label>
                <Select
                  value={formData.stringBrand || ''}
                  onValueChange={(value) => updateField('stringBrand', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {STRING_BRANDS.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.stringType || ''}
                  onValueChange={(value) => updateField('stringType', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {STRING_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tension</Label>
              <Select
                value={formData.stringTension || ''}
                onValueChange={(value) => updateField('stringTension', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tension" />
                </SelectTrigger>
                <SelectContent>
                  {STRING_TENSIONS.map(tension => (
                    <SelectItem key={tension} value={tension}>{tension}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Shoes Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Footprints className="w-5 h-5" />
              Shoes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand</Label>
                <Select
                  value={formData.shoeBrand || ''}
                  onValueChange={(value) => updateField('shoeBrand', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {SHOE_BRANDS.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={formData.shoeModel || ''}
                  onValueChange={(value) => updateField('shoeModel', value)}
                  disabled={!formData.shoeBrand}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.shoeBrand ? "Select model" : "Select brand first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableShoeModels.map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Size</Label>
              <Select
                value={formData.shoeSize || ''}
                onValueChange={(value) => updateField('shoeSize', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {SHOE_SIZES.map(size => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Playing Style Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Hand className="w-5 h-5" />
              Playing Style
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Style</Label>
                <Select
                  value={formData.playingStyle || ''}
                  onValueChange={(value) => updateField('playingStyle', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAYING_STYLES.map(style => (
                      <SelectItem key={style.value} value={style.value}>
                        {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dominant Hand</Label>
                <Select
                  value={formData.dominantHand || ''}
                  onValueChange={(value) => updateField('dominantHand', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select hand" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOMINANT_HANDS.map(hand => (
                      <SelectItem key={hand.value} value={hand.value}>
                        {hand.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Backhand Type</Label>
              <Select
                value={formData.backhandType || ''}
                onValueChange={(value) => updateField('backhandType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select backhand type" />
                </SelectTrigger>
                <SelectContent>
                  {BACKHAND_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={cancelEditing} disabled={isUpdating}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            <Check className="w-4 h-4 mr-2" />
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    );
  }

  // View mode
  const hasAnyData = profile && (
    profile.racquetBrand || profile.racquetModel ||
    profile.stringBrand || profile.stringType ||
    profile.shoeBrand || profile.shoeModel ||
    profile.playingStyle || profile.dominantHand
  );

  return (
    <div className="space-y-4">
      {/* Edit Button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={startEditing}>
          <Pencil className="w-4 h-4 mr-2" />
          {hasAnyData ? 'Edit Profile' : 'Add Gear Info'}
        </Button>
      </div>

      {!hasAnyData ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Backpack className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No gear information added yet</p>
            <p className="text-sm text-muted-foreground">
              Add your racquet, strings, shoes, and playing style
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Racquet Card */}
          {(profile?.racquetBrand || profile?.racquetModel) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Backpack className="w-5 h-5 text-green-600" />
                  Racquet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Brand</p>
                    <p className="font-medium">{profile?.racquetBrand || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Model</p>
                    <p className="font-medium">{profile?.racquetModel || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Grip Size</p>
                    <p className="font-medium">{profile?.gripSize || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strings Card */}
          {(profile?.stringBrand || profile?.stringType) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CircleDot className="w-5 h-5 text-blue-600" />
                  Strings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Brand</p>
                    <p className="font-medium">{profile?.stringBrand || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Type</p>
                    <p className="font-medium">{profile?.stringType || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Tension</p>
                    <p className="font-medium">{profile?.stringTension || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Shoes Card */}
          {(profile?.shoeBrand || profile?.shoeModel) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Footprints className="w-5 h-5 text-orange-600" />
                  Shoes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Brand</p>
                    <p className="font-medium">{profile?.shoeBrand || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Model</p>
                    <p className="font-medium">{profile?.shoeModel || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Size</p>
                    <p className="font-medium">{profile?.shoeSize || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Playing Style Card */}
          {(profile?.playingStyle || profile?.dominantHand) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Hand className="w-5 h-5 text-purple-600" />
                  Playing Style
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profile?.playingStyle && (
                    <Badge variant="secondary" className="text-sm py-1 px-3">
                      {PLAYING_STYLES.find(s => s.value === profile.playingStyle)?.label || profile.playingStyle}
                    </Badge>
                  )}
                  {profile?.dominantHand && (
                    <Badge variant="outline" className="text-sm py-1 px-3">
                      {DOMINANT_HANDS.find(h => h.value === profile.dominantHand)?.label || profile.dominantHand}-Handed
                    </Badge>
                  )}
                  {profile?.backhandType && (
                    <Badge variant="outline" className="text-sm py-1 px-3">
                      {BACKHAND_TYPES.find(b => b.value === profile.backhandType)?.label || profile.backhandType} Backhand
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
